/**
 * Simple Angular controller that is attached to the GridPurchaseOrders within the index.aspx. The purpose of this is to allow the user to easily
 * close Open Purchase Orders. This is important for a lot of our users due to inconsistent work done by Bookkeepers. Now whenever purchase order(s)
 * are left behind they can just easily multi-select the ones they want to close instead of having to find each Purchase Order individually and close them.
 */
/* @ngInject */ export function bulkPurchaseOrderController(
  $scope,
  Alert,
  BooksSvc,
  BusyScreen,
  PurchaseOrdersService,
  uiGridConstants
) {
  const alertTitle = 'Close Purchase Orders';

  const COLUMNS = [
    { field: 'poNumber', displayName: 'PO #', width: 80 },
    { field: 'memo', cellTooltip: true },
    { field: 'entityName', displayName: 'For', cellTooltip: true },
    {
      field: 'entryDate',
      cellFilter: 'date:"M/d/yyyy"',
      filterCellFiltered: true,
      width: 150,
      displayName: 'Date Created'
    },
    {
      field: 'amount',
      cellTemplate:
        '<div class="ui-grid-cell-contents">${{COL_FIELD.toFixed(2)}}</div>'
    },
    {
      field: 'amountPaid',
      cellTemplate:
        '<div class="ui-grid-cell-contents">${{COL_FIELD.toFixed(2)}}</div>'
    },
    {
      field: 'dateRequired',
      cellFilter: 'date:"M/d/yyyy"',
      filterCellFiltered: true,
      width: 150,
      displayName: 'Date Required'
    }
  ];

  const gridOptions = {
    data: 'dbPurchaseOrders',
    enableColumnMenus: false,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableFiltering: true,
    enableRowSelection: true,
    enableSelectAll: true,
    minRowsToShow: 3,
    multiSelect: true,
    enableFullRowSelection: true,
    enableColumnResizing: true,
    showGridFooter: true,
    /**
     * We register the grid api to the $scope so we have reference to which rows are clicked and that way we can interact with them in this file.
     */
    onRegisterApi: function (gridApi) {
      $scope.gridApi = gridApi;

      gridApi.selection.on.rowSelectionChanged($scope, function (row) {
        const selectedRows = gridApi.selection.getSelectedRows();

        $scope.verifySingleSelect(selectedRows.length, row, alertTitle, 1000);
      });
      gridApi.selection.on.rowSelectionChangedBatch($scope, function (rows) {
        const selectedRows = gridApi.selection.getSelectedRows();

        $scope.verifyMultiSelect(selectedRows.length, rows, alertTitle, 1000);
      });
    },
    columnDefs: COLUMNS
  };

  /**
   * Our grid options for the Purchase Orders are named purchaseOrderOptions, so in order for this to interact with the grid we need to set that within
   * the $scope.
   */
  $scope.purchaseOrderOptions = gridOptions;

  /**
   * Takes the list of selected rows and maps them to their ID's. This is used to pass to the server to close the purchase orders by their ID's.
   *
   * This checks to see if there are any selected rows. If there are then we can save, if not then we cannot save.
   *
   * @returns void
   */
  function closePurchaseOrders() {
    var selectedRows = $scope.gridApi.selection.getSelectedRows();

    if (!selectedRows.length) {
      Alert.info(
        'No Purchase Order(s) have been selected. Please select one or more before continuing.',
        alertTitle
      );
      return;
    }

    const purchaseOrdersToClose = selectedRows.map((po) => po.id);

    BusyScreen.setBusy(true);

    PurchaseOrdersService.bulkClosePurchaseOrders(purchaseOrdersToClose)
      .then(() => BooksSvc.getOpenPurchaseOrders())
      .then((resp) => {
        // Refresh the purchase order data now that we have closed PO's
        $scope.dbPurchaseOrders = resp.data.sortOn('poNumber');
        BusyScreen.setBusy(false);
        Alert.success(
          'The selected purchase order(s) have been closed.',
          alertTitle
        );
      })
      .catch((error) => {
        BusyScreen.setBusy(false);

        const errorMesssage =
          error.parsedResponse && error.parsedResponse.message()
            ? error.parsedResponse.message()
            : 'There was an error closing the purchase order(s). Please try again.';

        Alert.error(errorMesssage, alertTitle);
      });
  }

  /**
   * Simple function that checks to see if there are any selected rows. If there are then we can save, if not then we cannot save.
   * If there are no selected rows then the user has not selected anything to save.
   *
   * @returns boolean (true = can Save; false = cannot save)
   */
  function canSavePurchaseOrders() {
    return $scope.gridApi.selection.getSelectedRows().length > 0;
  }

  /**
   * Attach these to the $scope so they can be used in the index file.
   */
  $scope.closePurchaseOrders = closePurchaseOrders;
  $scope.canSavePurchaseOrders = canSavePurchaseOrders;
}
